import { create } from 'zustand';
import { OnboardingData, ProductData, VisualContent, AdditionalOffers, VisualStyle, SalesAngle, ContextData, MultiFunnelData, FunnelConfig, FunnelSection } from '../types/onboarding';
import { axiosInstanceWebsite } from '../services/instance';
import { SectionTemplate, getSectionTemplates } from '../services/funnels';

export enum UploadedMethodType {
  LINK = 'link',
  MANUAL = 'manual',
  PREVIOUS = 'previous',
  NONE = 'none',
}

const initialProductData: ProductData = {
  name: '',
  price: '',
  details: '',
  warranty: '30days',
  freeShipping: true,
  isManualEntry: false,
  productLink: '',
  customWarrantyDays: '',
  uploadedMethod: UploadedMethodType.LINK,
  variants: null,
};

const initialVisualContent: VisualContent = {
  imagesBase64: [],
  images: [],
  selectedAngle: [],
};

const initialAdditionalOffers: AdditionalOffers = {
  bumpOffer: {
    enabled: false,
    productName: '',
    price: '',
    image: "",
    url_images: "",
    description: '',
    isManualEntry: false,
    productLink: '',
    externalId: null,
    providerId: null,
    uploadedMethod: UploadedMethodType.NONE,
  },
  upsell: {
    enabled: false,
    productName: '',
    price: '',
    image: undefined,
    products: [],
  },
  downsell: {
    enabled: false,
    discountPercentage: '',
  },
};

const initialVisualStyle: VisualStyle = {
  colorPalette: '',
  brandName: '',
  useAiLogo: true,
};

const initialContextData: ContextData = {
  pain_detection: '',
  buyer_detection: '',
  recommended_sales_angles: [],
};

// Standard structural sections (always available regardless of design)
// Order: CTA early (2-3), Guarantee mid (5-6), FAQ last
const STANDARD_SECTIONS: FunnelSection[] = [
  { id: 'cta', name: 'Llamada a la acción', icon: 'shopping-cart', enabled: true, order: 0, isStandard: true },
  { id: 'warranty', name: 'Garantía', icon: 'shield', enabled: true, order: 1, isStandard: true },
  { id: 'faq', name: 'Preguntas frecuentes', icon: 'help-circle', enabled: true, order: 2, isStandard: true },
];

// Map section_type to a readable display name
const SECTION_TYPE_DISPLAY_NAMES: Record<string, string> = {
  'portada': 'Portada',
  'beneficios-destacados': 'Beneficios Destacados',
  'beneficios': 'Beneficios',
  'selector-cantidades': 'Selector de Cantidades',
  'antes-y-despues': 'Antes y Después',
  'visualizaciones-datos': 'Visualización de Datos',
  'hecho-de': 'Hecho de',
  'como-usar': 'Cómo Usar',
  'calificacion': 'Calificación',
  'testimonios': 'Testimonios',
  'objeciones': 'Objeciones',
  'tabla-comparativa': 'Tabla Comparativa',
  'ebook': 'Ebook / Lead Magnet',
};

type OnboardingStore = {
  data: OnboardingData;
  setStep: (step: number) => void;
  nextStep: () => void;
  previousStep: () => void;
  updateProductData: (data: Partial<ProductData>) => void;
  updateVisualContent: (data: Partial<VisualContent>) => void;
  updateAdditionalOffers: (data: Partial<AdditionalOffers>) => void;
  updateVisualStyle: (data: Partial<VisualStyle>) => void;
  resetOnboardingData: () => void;
  salesAngles: SalesAngle[];
  hasFetchedSalesAngles: boolean;
  setSalesAngles: (salesAngles: SalesAngle[]) => void;
  setHasFetchedSalesAngles: (hasFetchedSalesAngles: boolean) => void;
  previousProducts: any[];
  setPreviousProducts: (products: any[]) => void;
  isPreviousProductsLoading: boolean;
  setIsPreviousProductsLoading: (isPreviousProductsLoading: boolean) => void;
  fetchPreviousProducts: () => Promise<void>;
  contextData: ContextData;
  updateContextData: (data: Partial<ContextData>) => void;
  priceErrors: { main?: string; bump?: string; upsell?: string };
  setPriceErrors: (errors: { main?: string; bump?: string; upsell?: string }) => void;
  clearPriceErrors: () => void;
  isCustomSectionsOnly: boolean;
  setIsCustomSectionsOnly: (value: boolean) => void;
  showCustomSectionsToggle: boolean;
  setShowCustomSectionsToggle: (value: boolean) => void;
  multiFunnelData: MultiFunnelData;
  setFunnelCount: (count: number) => void;
  updateFunnelConfig: (funnelIndex: number, config: Partial<FunnelConfig>) => void;
  updateFunnelSection: (funnelIndex: number, sectionId: string, updates: Partial<FunnelSection>) => void;
  reorderFunnelSections: (funnelIndex: number, sections: FunnelSection[]) => void;
  initializeFunnels: (count: number) => void;
  addFunnel: () => void;
  removeFunnel: (funnelIndex: number) => void;
  setFunnelDesign: (funnelIndex: number, templateId: string, templateType: string) => Promise<void>;
  isFetchingDesignSections: boolean;
  sectionTemplates: SectionTemplate[];
  isFetchingTemplates: boolean;
  hasFetchedTemplates: boolean;
  fetchSectionTemplates: () => Promise<void>;
};

export const useOnboardingStore = create<OnboardingStore>((set, get) => ({
  data: {
    step: 1,
    productData: initialProductData,
    visualContent: initialVisualContent,
    additionalOffers: initialAdditionalOffers,
    visualStyle: initialVisualStyle,
    isManualEntry: false,
    productLink: '',
  },
  salesAngles: [],
  hasFetchedSalesAngles: false,
  previousProducts: [],
  isPreviousProductsLoading: false,
  setIsPreviousProductsLoading: (isPreviousProductsLoading: boolean) => set({ isPreviousProductsLoading }),
  contextData: initialContextData,
  setStep: (step) => set((state) => ({ data: { ...state.data, step } })),
  nextStep: () => set((state) => {
    const maxStep = state.isCustomSectionsOnly ? 6 : 4;
    return { data: { ...state.data, step: Math.min(state.data.step + 1, maxStep) } };
  }),
  previousStep: () => set((state) => ({ data: { ...state.data, step: Math.max(state.data.step - 1, 1) } })),
  updateProductData: (productData) => 
    set((state) => {
      const newProductData = { ...state.data.productData, ...productData };
      return { 
        data: { 
          ...state.data, 
          productData: newProductData 
        }
      };
    }),
  updateVisualContent: (visualContent: Partial<VisualContent>) => 
    set((state) => {
      const newVisualContent = { ...state.data.visualContent, ...visualContent };
      return {
        data: { 
          ...state.data, 
          visualContent: newVisualContent
        }
      };
    }),
  updateAdditionalOffers: (additionalOffers) => 
    set((state) => {
      const updatedOffers = { ...state.data.additionalOffers };
      
      if (additionalOffers.bumpOffer) {
        updatedOffers.bumpOffer = {
          ...updatedOffers.bumpOffer,
          ...additionalOffers.bumpOffer
        };
      }
      
      if (additionalOffers.upsell) {
        updatedOffers.upsell = {
          ...updatedOffers.upsell,
          ...additionalOffers.upsell,
          // Handle nested products array if present
          products: additionalOffers.upsell.products || updatedOffers.upsell.products
        };
      }
      
      // Handle downsell specifically
      if (additionalOffers.downsell) {
        updatedOffers.downsell = {
          ...updatedOffers.downsell,
          ...additionalOffers.downsell
        };
      }
      
      return { 
        data: { 
          ...state.data, 
          additionalOffers: updatedOffers 
        } 
      };
    }),
  updateVisualStyle: (visualStyle) => 
    set((state) => ({ 
      data: { 
        ...state.data, 
        visualStyle: { ...state.data.visualStyle, ...visualStyle } 
      } 
    })),
  resetOnboardingData: () => set({
    data: {
      step: 1,
      productData: initialProductData,
      visualContent: initialVisualContent,
      additionalOffers: initialAdditionalOffers,
      visualStyle: initialVisualStyle,
      isManualEntry: false,
      productLink: '',
    },
    salesAngles: [],
    hasFetchedSalesAngles: false,
    previousProducts: [],
    isPreviousProductsLoading: false,
    contextData: initialContextData,
    priceErrors: {},
    isCustomSectionsOnly: false,
    multiFunnelData: { funnelCount: 3, funnels: [] },
  }),
  priceErrors: {},
  setPriceErrors: (errors) => set({ priceErrors: errors }),
  clearPriceErrors: () => set({ priceErrors: {} }),
  isCustomSectionsOnly: false,
  setIsCustomSectionsOnly: (value: boolean) => set({ isCustomSectionsOnly: value }),
  showCustomSectionsToggle: false,
  setShowCustomSectionsToggle: (value: boolean) => set({ showCustomSectionsToggle: value }),
  multiFunnelData: { funnelCount: 3, funnels: [] },
  setFunnelCount: (count: number) => set((state) => ({
    multiFunnelData: { ...state.multiFunnelData, funnelCount: count }
  })),
  updateFunnelConfig: (funnelIndex: number, config: Partial<FunnelConfig>) => set((state) => {
    const funnels = [...state.multiFunnelData.funnels];
    if (funnels[funnelIndex]) {
      funnels[funnelIndex] = { ...funnels[funnelIndex], ...config };
    }
    return { multiFunnelData: { ...state.multiFunnelData, funnels } };
  }),
  updateFunnelSection: (funnelIndex: number, sectionId: string, updates: Partial<FunnelSection>) => set((state) => {
    const funnels = [...state.multiFunnelData.funnels];
    if (funnels[funnelIndex]) {
      funnels[funnelIndex] = {
        ...funnels[funnelIndex],
        sections: funnels[funnelIndex].sections.map(s =>
          s.id === sectionId ? { ...s, ...updates } : s
        )
      };
    }
    return { multiFunnelData: { ...state.multiFunnelData, funnels } };
  }),
  reorderFunnelSections: (funnelIndex: number, sections: FunnelSection[]) => set((state) => {
    const funnels = [...state.multiFunnelData.funnels];
    if (funnels[funnelIndex]) {
      funnels[funnelIndex] = { ...funnels[funnelIndex], sections };
    }
    return { multiFunnelData: { ...state.multiFunnelData, funnels } };
  }),
  initializeFunnels: (count: number) => set((state) => {
    const brandName = state.data.visualStyle.brandName?.trim() || 'Embudo';
    const funnels: FunnelConfig[] = Array.from({ length: count }, (_, i) => ({
      id: `funnel-${i + 1}`,
      name: `${brandName} ${i + 1}`,
      designTemplate: null,
      designTemplateType: null,
      sections: STANDARD_SECTIONS.map((s, idx) => ({ ...s, order: idx })),
      selectedAngles: [],
    }));
    return { multiFunnelData: { funnelCount: count, funnels } };
  }),
  addFunnel: () => set((state) => {
    const { funnels } = state.multiFunnelData;
    if (funnels.length >= 5) return state;
    const brandName = state.data.visualStyle.brandName?.trim() || 'Embudo';
    const newIndex = funnels.length + 1;
    const newFunnel: FunnelConfig = {
      id: `funnel-${Date.now()}`,
      name: `${brandName} ${newIndex}`,
      designTemplate: null,
      designTemplateType: null,
      sections: STANDARD_SECTIONS.map((s, idx) => ({ ...s, order: idx })),
      selectedAngles: [],
    };
    const newFunnels = [...funnels, newFunnel];
    return { multiFunnelData: { funnelCount: newFunnels.length, funnels: newFunnels } };
  }),
  removeFunnel: (funnelIndex: number) => set((state) => {
    const { funnels } = state.multiFunnelData;
    if (funnels.length <= 1) return state;
    const newFunnels = funnels.filter((_, i) => i !== funnelIndex);
    return { multiFunnelData: { funnelCount: newFunnels.length, funnels: newFunnels } };
  }),
  isFetchingDesignSections: false,
  setFunnelDesign: async (funnelIndex: number, templateId: string, templateType: string) => {
    set({ isFetchingDesignSections: true });
    try {
      // Fetch all sections for this design type (excluding preview)
      const allSections = await getSectionTemplates('type', templateType);
      const designSections = allSections.filter(s => s.section_type !== 'preview');

      // Convert API sections to FunnelSection format
      const apiSections: FunnelSection[] = designSections.map((s, idx) => ({
        id: `design-${s.section_type}-${s.id}`,
        name: SECTION_TYPE_DISPLAY_NAMES[s.section_type] || s.section_type,
        icon: 'image',
        enabled: true,
        order: idx,
        sectionType: s.section_type,
        url: s.url || undefined,
        isStandard: false,
      }));

      // Interleave standard sections among design sections:
      // CTA after position 2 (3rd element), Guarantee after position 5 (6th element), FAQ at the end
      const CTA_INSERT_AFTER = 2;       // After the 2nd design section (index 2 = 3rd position)
      const WARRANTY_INSERT_AFTER = 5;  // After the 5th design section (index 5 = 6th position)

      const ctaSection = STANDARD_SECTIONS.find(s => s.id === 'cta')!;
      const warrantySection = STANDARD_SECTIONS.find(s => s.id === 'warranty')!;
      const faqSection = STANDARD_SECTIONS.find(s => s.id === 'faq')!;

      const allFunnelSections: FunnelSection[] = [];
      let orderCounter = 0;

      for (let i = 0; i < apiSections.length; i++) {
        allFunnelSections.push({ ...apiSections[i], order: orderCounter++ });

        // Insert CTA after the 2nd design section
        if (i === CTA_INSERT_AFTER && apiSections.length > CTA_INSERT_AFTER) {
          allFunnelSections.push({ ...ctaSection, order: orderCounter++ });
        }
        // Insert Warranty after the 5th design section
        if (i === WARRANTY_INSERT_AFTER && apiSections.length > WARRANTY_INSERT_AFTER) {
          allFunnelSections.push({ ...warrantySection, order: orderCounter++ });
        }
      }

      // If there weren't enough design sections, add CTA/Warranty at the end (before FAQ)
      if (apiSections.length <= CTA_INSERT_AFTER) {
        allFunnelSections.push({ ...ctaSection, order: orderCounter++ });
      }
      if (apiSections.length <= WARRANTY_INSERT_AFTER) {
        allFunnelSections.push({ ...warrantySection, order: orderCounter++ });
      }

      // FAQ always goes at the very end
      allFunnelSections.push({ ...faqSection, order: orderCounter++ });

      // Update the funnel config
      set((state) => {
        const funnels = [...state.multiFunnelData.funnels];
        if (funnels[funnelIndex]) {
          funnels[funnelIndex] = {
            ...funnels[funnelIndex],
            designTemplate: templateId,
            designTemplateType: templateType,
            sections: allFunnelSections,
          };
        }
        return { multiFunnelData: { ...state.multiFunnelData, funnels }, isFetchingDesignSections: false };
      });
    } catch (error) {
      console.error('Error fetching design sections:', error);
      // Still set the template even if section fetch fails
      set((state) => {
        const funnels = [...state.multiFunnelData.funnels];
        if (funnels[funnelIndex]) {
          funnels[funnelIndex] = {
            ...funnels[funnelIndex],
            designTemplate: templateId,
            designTemplateType: templateType,
          };
        }
        return { multiFunnelData: { ...state.multiFunnelData, funnels }, isFetchingDesignSections: false };
      });
    }
  },
  sectionTemplates: [],
  isFetchingTemplates: false,
  hasFetchedTemplates: false,
  fetchSectionTemplates: async () => {
    const state = get();
    if (state.hasFetchedTemplates || state.isFetchingTemplates) return;
    set({ isFetchingTemplates: true });
    try {
      const templates = await getSectionTemplates('section_type', 'preview');
      set({ sectionTemplates: templates, hasFetchedTemplates: true, isFetchingTemplates: false });
    } catch (error) {
      console.error('Error fetching section templates:', error);
      set({ isFetchingTemplates: false });
    }
  },
  setSalesAngles: (salesAngles: SalesAngle[]) =>
    set({ salesAngles }),
  setHasFetchedSalesAngles: (hasFetchedSalesAngles: boolean) =>
    set({ hasFetchedSalesAngles }),
  updateContextData: (contextData) =>
    set((state) => ({
      contextData: { ...state.contextData, ...contextData }
    })),
  fetchPreviousProducts: async () => {
    const state = get();
    
    if (state.isPreviousProductsLoading) {
      return;
    }
    if (state.previousProducts.length === 0) {
      set({ isPreviousProductsLoading: true });
      try {
        const response = await axiosInstanceWebsite.get('/store/product');
        set({ 
          previousProducts: response.data.reverse() || [],
          isPreviousProductsLoading: false
        });
      } catch (err) {
        console.error('Error fetching previous products:', err);
        set({ 
          previousProducts: [],
          isPreviousProductsLoading: false 
        });
      }
    }
  },
  setPreviousProducts: (products) => set({ previousProducts: products }),
}));